import React, { useEffect, useState } from "react";
import { DatePicker, DatePickerProps } from "@boi/react-components";
import type { DateValue } from 'react-aria-components';
import { CalendarDate } from '@internationalized/date';
import { I18nProvider } from "react-aria-components";

import Container from "../forms/controls/container";
import DatePickerPostfix from "./DatePickerPostfix";
import { iControlProps } from "../forms/types";
import filterDates from "./functions/filterDates";
import findMinDate from "./functions/findMinDate";
import findMaxDate from "./functions/findMaxDate";

type Props = iControlProps & DatePickerProps<DateValue> & {
    onChange: (date: string) => void;
    error: string;
    errorMessage?: string;
    description?: string;
    placeholder?: string;
    only_date_in_past?: boolean;
    only_date_in_future?: boolean;
    include_today_in_dates?: boolean;
    exclude_weekends?: boolean;
    min_date_rule?: string;
    max_date_rule?: string;
    min_date?: string;
    max_date?: string;
    excluded_dates?: string;
    hide_calendar_icon?: boolean;
    dateFormat?: string;
};

export const convertStringToDate = (inputString: string): Date => {
    if (inputString.includes("-")) {
        const [year, month, day] = inputString.split("-").map(Number);
        return new Date(year, month - 1, day);
    } else {
        const [day, month, year] = inputString.split("/").map(Number);
        return new Date(year, month - 1, day);
    }
}

export const convertDateToString = (date: Date): string => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).padStart(4, '0');

    return `${year}-${month}-${day}`;
}

const convertToCalendarDate = (date: Date): CalendarDate =>
    new CalendarDate(
        date.getFullYear(),
        date.getMonth() + 1,
        date.getDate()
      );

const convertToDate = (dateValue: CalendarDate): Date => {
    const date = new Date();
    date.setFullYear(
        dateValue.year,
        dateValue.month - 1,
        dateValue.day
      );

    return date;
}

const DateV2 = (props: Props) => {
    const {
        onChange,
        hide_calendar_icon: hideCalendarIcon,
        error: propsError,
        value: propsValue,
    } = props;
    const [error, setError] = useState<string>(propsError);
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [date, setDate] = useState<CalendarDate | null>(propsValue === "" ? null : convertToCalendarDate(convertStringToDate(propsValue)));

    // If datepicker is disabled then validation rules handles the filtered dates in the datepicker
    const minDate = !hideCalendarIcon ? findMinDate(props) : null;
    const maxDate = !hideCalendarIcon ? findMaxDate(props) : null;
    
    const filterDate = (date: Date) =>
        !hideCalendarIcon ? filterDates(date, props) : true;

    useEffect(() => {
        setError(propsError);
    }, [propsError]);

    const minCalendarDate = minDate ? convertToCalendarDate(minDate) : null;
    const maxCalendarDate = maxDate ? convertToCalendarDate(maxDate) : null;
    const locale = "en-IE";

    const onBlur = (newDate: any) => {
        const isNotAvaliable = ((minCalendarDate !== null && date) && date.compare(minCalendarDate) < 0) || 
            ((maxCalendarDate !== null && date) && date.compare(maxCalendarDate) > 0) || 
            !filterDate(convertToDate(date as CalendarDate))

        if (isNotAvaliable) {
            setError(propsError)
        }
        date && !isNotAvaliable ? onChange(convertDateToString(convertToDate(date))) : onChange("");
    };

    return (
        <Container {...props} error={error}>
            <DatePickerPostfix
                isActive={isOpen}
                onClick={() => setIsOpen(!isOpen)}
                hideCalendarIcon
            >
                <I18nProvider locale={locale}>
                    <DatePicker
                        isOpen={isOpen}
                        withCalendar={!hideCalendarIcon}
                        onOpenChange={(e) => { setIsOpen(e) }}
                        value={date}
                        onChange={(newDate) => {
                            const isNotAvaliable = ((minCalendarDate !== null && newDate) && newDate.compare(minCalendarDate) < 0) || 
                                ((maxCalendarDate !== null && newDate) && newDate.compare(maxCalendarDate) > 0) || 
                                !filterDate(convertToDate(newDate as CalendarDate))

                            if (isNotAvaliable) {
                                setError(propsError)
                            }

                            newDate && !isNotAvaliable ? onChange(convertDateToString(convertToDate(newDate))) : onChange("");
                            setDate(newDate)
                        }}
                        onBlur={onBlur}
                        minValue={minCalendarDate}
                        maxValue={maxCalendarDate}
                        isDateUnavailable={(dateValue: DateValue) => 
                            (minCalendarDate !== null && dateValue.compare(minCalendarDate) < 0) || 
                            (maxCalendarDate !== null && dateValue.compare(maxCalendarDate) > 0) || 
                            !filterDate(convertToDate(dateValue as CalendarDate))}
                    />
                </I18nProvider>
            </DatePickerPostfix>
        </Container>
    );
};

export default DateV2;
